import { ReactNode } from "react";
import { Box, Link, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IconError } from "src/components/icons-mui/error";
import { IconWikifolioLogo } from "src/components/icons-mui/wikifolio-logo";
import { Button } from "src/components-mui/base/button/button";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { Container } from "src/components-mui/layout/container";
import { getGlobals } from "src/constants/globals";

const ErrorImage = () => <IconError sx={{ width: ["100%", "auto"], height: ["unset", "328px"] }} />;

interface IProps {
    subHeadline: string;
    children?: ReactNode;
    showLogo?: boolean;
    staticTranslations?: (key: string) => string;
}

const ERROR_COMMON_HOME_BUTTON_TEST_ID = "error-common-home-button";
const ERROR_COMMON_SEARCH_BUTTON_TEST_ID = "error-common-search-button";

export const ErrorCommon = ({ subHeadline, children, showLogo, staticTranslations }: IProps) => {
    const globals = getGlobals();
    const { t } = useTranslation("common");

    const activeT = staticTranslations ?? t;

    const searchExtendedRoute = globals?.routes.searchExtended;

    return (
        <Container sx={{ mt: [2, 3, "128px"], mb: [7, 7, "128px"] }}>
            <Stack>
                {showLogo ? (
                    <Link
                        href="/"
                        aria-label="wikifolio Financial Technologies"
                        title="wikifolio Financial Technologies"
                        sx={{ display: "block", mb: 4, alignSelf: ["center", "center", "flex-start"] }}
                    >
                        <IconWikifolioLogo sx={{ width: "auto", height: ["24px", "24px", "32px"], color: "green.600" }} />
                    </Link>
                ) : (
                    <Box
                        sx={{
                            height: ["32px", "32px", "0px"],
                            mb: [4, 4, 0],
                        }}
                    />
                )}
                <Stack
                    direction={["column", "column", "row"]}
                    sx={{
                        justifyContent: "space-between",
                    }}
                >
                    <Stack
                        sx={{
                            alignItems: ["center", "center", "flex-start"],
                        }}
                    >
                        <WfHeading component="h1" fontSize={{ xs: "4xl", sm: "4xl", md: "6xl" }} sx={{ textAlign: ["center", "center", "unset"] }}>
                            {activeT("errors.pages.headline")}
                        </WfHeading>
                        <WfHeading
                            component="h2"
                            fontSize={{ xs: "2xl", sm: "2xl", md: "3xl", lg: "4xl" }}
                            sx={{ mt: 2, fontWeight: "bold", textAlign: ["center", "center", "unset"] }}
                        >
                            {subHeadline}
                        </WfHeading>
                        {children}
                        <Stack
                            direction={["column", "row"]}
                            sx={{
                                mt: 5,
                                gap: 2,
                                width: ["100%", "unset"],
                            }}
                        >
                            <Button href="/" variant={searchExtendedRoute ? "outlined" : "contained"} data-test-id={ERROR_COMMON_HOME_BUTTON_TEST_ID}>
                                {activeT("buttons.home-page")}
                            </Button>
                            {searchExtendedRoute && (
                                <Button href={searchExtendedRoute} data-test-id={ERROR_COMMON_SEARCH_BUTTON_TEST_ID}>
                                    {activeT("search-wikifolios")}
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                    <Stack
                        sx={{
                            justifyContent: "center",
                            alignItems: "center",
                            mt: [5, 10, 0],
                            ml: [0, 0, 0, "264px"],
                        }}
                    >
                        <ErrorImage />
                    </Stack>
                </Stack>
            </Stack>
        </Container>
    );
};
